import { ValuedColorsProvider } from "@omnia/shared/color/provider"
import { LayerTypeId, NutrientType } from "@omnia/shared/generated/enums"
import { labels } from "@omnia/shared/generated/labels"
import {
    IDatalayerMapInstance,
    IDatalayerZoneMapInstance,
    INumericValuesViewerOptions,
    ZoneTextLocation,
} from "@omnia/shared/layers/types"
import { NumericValuesViewer } from "@omnia/shared/layers/viewers/numeric"
import { getNutrientColorProvider } from "@omnia/shared/nutrition/colors"
import { isNumber } from "@omnia/shared/utils/utils"
import { INutrientAppLayer } from "../types"
import { NutrientAppZone } from "./nutrientAppZone"

export class NutrientAppLayerViewerFactory {
    static create(nutrientType?: NutrientType, showUnits?: boolean, showNutrientRates?: boolean) {
        let colorProvider

        if (isNumber(nutrientType)) {
            let key = NutrientType[nutrientType]
            colorProvider = getNutrientColorProvider(key, true)
        }

        let factory = {
            typeId: LayerTypeId.NutrientApplications,

            name: "Nutrient applications",

            createViewer: (layer) =>
                NutrientAppLayerViewerFactory.createViewer(
                    layer,
                    nutrientType,
                    colorProvider,
                    showUnits,
                    showNutrientRates
                ),

            colors: colorProvider ? colorProvider.getColors() : null,
        }

        return factory
    }

    static createViewer(
        layer: IDatalayerMapInstance,
        targetNutrient?: NutrientType,
        colorProvider?: ValuedColorsProvider,
        showUnits?: boolean,
        showNutrientRates?: boolean
    ) {
        let appLayer = <INutrientAppLayer>layer.datalayer

        if (!targetNutrient) {
            targetNutrient = appLayer.Application.TargetNutrient
        }

        if (!colorProvider) {
            let key = NutrientType[targetNutrient]
            colorProvider = getNutrientColorProvider(key)
        }

        var units = ""

        if (showUnits !== false) {
            units = showNutrientRates ? "kg" : labels.FertiliserAppUnit(appLayer.Application.Units)
            if (units) units += "/ha"
        }

        var options = <INumericValuesViewerOptions>{
            colorProvider,
            units,
            valueFilter: (value) => value > 0,
        }

        return new NutrientAppLayerViewer(layer, options, appLayer, showNutrientRates)
    }
}

export class NutrientAppLayerViewer extends NumericValuesViewer {
    constructor(
        layer: IDatalayerMapInstance,
        options: INumericValuesViewerOptions,
        private _appLayer: INutrientAppLayer,
        private _showNutrientRates: boolean
    ) {
        super(layer, options)
    }

    public getZoneValue(zone: { value: any }): number {
        if (zone instanceof NutrientAppZone) {
            return this._showNutrientRates ? zone.nutrientRate : zone.productRate
        }
    }

    getZoneText(zone: IDatalayerZoneMapInstance, location: ZoneTextLocation): string {
        if (zone instanceof NutrientAppZone) {
            let value = this._showNutrientRates ? zone.nutrientRate : zone.productRate

            let text = this.getValueText(value, location)

            if (zone.hasUserDefinedProductRate()) {
                text += " *"
            }

            return text
        }
    }

    getValueText(value: number, location?: ZoneTextLocation): string {
        var text = super.getValueText(value, location)

        if (location == ZoneTextLocation.MapTooltip) {
            if (text) {
                text = this._appLayer.Application.ProductName + ": " + text
            }
        }

        return text
    }

    public getValueFillColor(value: number, alpha: number = 1) {
        if (value <= 0) {
            return [255, 252, 255, alpha]
        }

        return super.getValueFillColor(value, alpha)
    }
}
